/**
 * The admin permission model, as the browser sees it.
 *
 * This is a mirror of backend/admin_auth.py and it is *only* used to decide
 * what to render — which sidebar entries appear, which buttons are shown. It
 * is not a control. Every admin endpoint re-checks its own permission against
 * the role stored in the database, so a client that ignores all of this and
 * calls a route anyway is still refused.
 *
 * Keeping that distinction sharp matters: the moment someone treats this file
 * as security, hiding a button becomes the only thing standing between a
 * support agent and a payout.
 */

export const ADMIN_ROLES = [
  "SUPER_ADMIN",
  "ADMIN",
  "FINANCE_ADMIN",
  "OPERATIONS_ADMIN",
  // The department desks. Their agents work the cases escalated to their
  // department, so they need the dashboard as much as Support does; the
  // backend has had these roles all along (modules/identity/authorization.py).
  "TICKETING_AGENT",
  "ACCOMMODATION_ADMIN",
  "TAXI_ADMIN",
  "CAR_ADMIN",
  "SUPPORT_AGENT",
  "VERIFICATION_AGENT",
  "READ_ONLY_ADMIN",
];

/**
 * Accounts created before the role model carry the lowercase "admin".
 * The backend maps it to ADMIN; so must the router, or every existing
 * administrator is locked out of the pages they had yesterday.
 */
export function normalizeRole(raw) {
  if (typeof raw !== "string") return null;
  const role = raw.trim().toUpperCase();
  if (role === "ADMIN") return "ADMIN";
  return ADMIN_ROLES.includes(role) ? role : null;
}

export const isAdmin = (user) => normalizeRole(user?.role) !== null;

/** `permissions` comes from GET /api/admin/me — the server's own answer. */
export const can = (permissions, domain, action = "read") =>
  Array.isArray(permissions) && permissions.includes(`${domain}:${action}`);
